import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Users, Clock, Trash2, UserPlus, UserMinus, Edit2, Search, Wrench, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const joursSemaine = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' },
];

const niveaux = [
  { value: 'debutant', label: 'Débutant' },
  { value: 'galop1-2', label: 'Galop 1-2' },
  { value: 'galop3-4', label: 'Galop 3-4' },
  { value: 'galop5-6', label: 'Galop 5-6' },
  { value: 'galop7+', label: 'Galop 7+' },
  { value: 'mixte', label: 'Tous niveaux' },
];

export const Groups = () => {
  const { user, isAdmin } = useAuth();
  const [groupes, setGroupes] = useState([]);
  const [themes, setThemes] = useState([]);
  const [instructeurs, setInstructeurs] = useState([]);
  const [cavaliers, setCavaliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedGroupe, setSelectedGroupe] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  
  const [newGroupe, setNewGroupe] = useState({
    nom: '',
    theme_principal_id: '',
    instructeur_id: '',
    jour_semaine: 0,
    heure_debut: '14:00',
    niveau: 'mixte',
    places_max: 8,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupesRes, themesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/groupes`),
        axios.get(`${API_URL}/api/themes`),
        axios.get(`${API_URL}/api/users`),
      ]);
      
      setGroupes(groupesRes.data);
      setThemes(themesRes.data);
      
      const users = usersRes.data;
      setInstructeurs(users.filter(u => u.role_club?.startsWith('instructeur')));
      setCavaliers(users.filter(u => u.role_club?.startsWith('cavalier') && u.statut === 'actif'));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Cleanup orphan member IDs from groups
  const handleCleanupGroups = async () => {
    setIsCleaningUp(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/cleanup-groups`);
      const { orphans_removed, groups_cleaned, message } = response.data;
      
      if (orphans_removed > 0) {
        toast.success(message);
        fetchData(); // Refresh data
      } else {
        toast.info('Aucun ID orphelin trouvé, les compteurs sont corrects');
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Erreur lors du nettoyage des groupes');
    } finally {
      setIsCleaningUp(false);
    }
  };

  const handleCreateGroupe = async () => {
    if (!newGroupe.nom || !newGroupe.theme_principal_id || !newGroupe.instructeur_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/groupes`, {
        ...newGroupe,
        cavaliers_membres: [],
        statut: 'actif'
      });
      toast.success('Groupe créé avec succès');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création du groupe');
    }
  };

  const handleUpdateGroupe = async () => {
    if (!selectedGroupe) return;
    
    try {
      await axios.put(`${API_URL}/api/groupes/${selectedGroupe.id}`, {
        ...selectedGroupe,
        nom: newGroupe.nom || selectedGroupe.nom,
        theme_principal_id: newGroupe.theme_principal_id || selectedGroupe.theme_principal_id,
        instructeur_id: newGroupe.instructeur_id || selectedGroupe.instructeur_id,
        jour_semaine: newGroupe.jour_semaine,
        heure_debut: newGroupe.heure_debut || selectedGroupe.heure_debut,
        niveau: newGroupe.niveau || selectedGroupe.niveau,
        places_max: newGroupe.places_max || selectedGroupe.places_max,
        notes: newGroupe.notes !== undefined ? newGroupe.notes : selectedGroupe.notes,
      });
      toast.success('Groupe modifié avec succès');
      setIsEditMode(false);
      setSelectedGroupe(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification du groupe');
    }
  };

  const handleAddMember = async (cavalier_id) => {
    if (!selectedGroupe) return;
    
    try {
      const res = await axios.post(`${API_URL}/api/groupes/${selectedGroupe.id}/members/${cavalier_id}?update_future_courses=true`);
      const updatedCount = res.data.updated_courses_count || 0;
      if (updatedCount > 0) {
        toast.success(`Cavalier ajouté au groupe. ${updatedCount} cours futur(s) mis à jour.`);
      } else {
        toast.success('Cavalier ajouté au groupe');
      }
      // Refresh the selected group
      const groupeRes = await axios.get(`${API_URL}/api/groupes/${selectedGroupe.id}`);
      setSelectedGroupe(groupeRes.data);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du membre');
    }
  };

  const handleRemoveMember = async (cavalier_id) => {
    if (!selectedGroupe) return;
    
    try {
      const res = await axios.delete(`${API_URL}/api/groupes/${selectedGroupe.id}/members/${cavalier_id}?update_future_courses=true`);
      const updatedCount = res.data.updated_courses_count || 0;
      if (updatedCount > 0) {
        toast.success(`Cavalier retiré du groupe. ${updatedCount} cours futur(s) mis à jour.`);
      } else {
        toast.success('Cavalier retiré du groupe');
      }
      // Refresh the selected group
      const groupeRes = await axios.get(`${API_URL}/api/groupes/${selectedGroupe.id}`);
      setSelectedGroupe(groupeRes.data);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du retrait du membre');
    }
  };

  const handleDeleteGroupe = async (force = false) => {
    if (!selectedGroupe) return;
    
    try {
      const response = await axios.delete(`${API_URL}/api/groupes/${selectedGroupe.id}?force=${force}`);
      toast.success(response.data.message);
      setSelectedGroupe(null);
      setDeleteConfirmOpen(false);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la suppression';
      if (errorMsg.includes('cours') && !force) {
        // Group is used by courses, ask for confirmation
        setDeleteConfirmOpen(true);
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const resetForm = () => {
    setNewGroupe({
      nom: '',
      theme_principal_id: '',
      instructeur_id: '',
      jour_semaine: 0,
      heure_debut: '14:00',
      niveau: 'mixte',
      places_max: 8,
      notes: ''
    });
  };

  const openEditMode = (groupe) => {
    setSelectedGroupe(groupe);
    setNewGroupe({
      nom: groupe.nom,
      theme_principal_id: groupe.theme_principal_id,
      instructeur_id: groupe.instructeur_id,
      jour_semaine: groupe.jour_semaine,
      heure_debut: groupe.heure_debut,
      niveau: groupe.niveau || 'mixte',
      places_max: groupe.places_max,
      notes: groupe.notes || ''
    });
    setIsEditMode(true);
  };

  const getTheme = (themeId) => themes.find(t => t.id === themeId);
  const getInstructeur = (instrId) => instructeurs.find(i => i.id === instrId);
  const getCavalier = (cavId) => cavaliers.find(c => c.id === cavId);

  const filteredGroupes = groupes.filter(g => 
    g.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (getTheme(g.theme_principal_id)?.nom_complet || getTheme(g.theme_principal_id)?.nom)?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableCavaliers = cavaliers.filter(c => 
    !selectedGroupe?.cavaliers_membres?.includes(c.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="groups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2E4A3D]">Groupes Fixes</h1>
          <p className="text-stone-500 mt-1">{groupes.length} groupe(s) configuré(s)</p>
        </div>
        
        <div className="flex gap-2">
          {/* Maintenance button for admins */}
          {isAdmin() && (
            <Button 
              variant="outline" 
              onClick={handleCleanupGroups}
              disabled={isCleaningUp}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              title="Corriger les compteurs de cavaliers"
              data-testid="cleanup-groups-btn"
            >
              {isCleaningUp ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wrench className="w-4 h-4 mr-2" />
              )}
              Maintenance
            </Button>
          )}
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-action" data-testid="new-groupe-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau groupe
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
              <DialogTitle className="font-serif">Créer un groupe fixe</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="form-label">Nom du groupe *</Label>
                <Input
                  value={newGroupe.nom}
                  onChange={(e) => setNewGroupe({...newGroupe, nom: e.target.value})}
                  placeholder="Ex: Galop 3 Mercredi"
                  className="input-field"
                  data-testid="groupe-nom"
                />
              </div>
              
              <div>
                <Label className="form-label">Thème principal *</Label>
                <Select 
                  value={newGroupe.theme_principal_id} 
                  onValueChange={(v) => setNewGroupe({...newGroupe, theme_principal_id: v})}
                >
                  <SelectTrigger data-testid="groupe-theme">
                    <SelectValue placeholder="Choisir un thème" />
                  </SelectTrigger>
                  <SelectContent>
                    {themes.map(theme => (
                      <SelectItem key={theme.id} value={theme.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: theme.couleur }}
                          />
                          {theme.nom_complet || theme.nom}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="form-label">Instructeur *</Label>
                <Select 
                  value={newGroupe.instructeur_id} 
                  onValueChange={(v) => setNewGroupe({...newGroupe, instructeur_id: v})}
                >
                  <SelectTrigger data-testid="groupe-instructeur">
                    <SelectValue placeholder="Choisir un instructeur" />
                  </SelectTrigger>
                  <SelectContent>
                    {instructeurs.map(instr => (
                      <SelectItem key={instr.id} value={instr.id}>
                        {instr.prenom} {instr.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Jour</Label>
                  <Select 
                    value={String(newGroupe.jour_semaine)} 
                    onValueChange={(v) => setNewGroupe({...newGroupe, jour_semaine: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {joursSemaine.map(jour => (
                        <SelectItem key={jour.value} value={String(jour.value)}>
                          {jour.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Heure</Label>
                  <Input
                    type="time"
                    value={newGroupe.heure_debut}
                    onChange={(e) => setNewGroupe({...newGroupe, heure_debut: e.target.value})}
                    className="input-field"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Niveau</Label>
                  <Select 
                    value={newGroupe.niveau} 
                    onValueChange={(v) => setNewGroupe({...newGroupe, niveau: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {niveaux.map(niv => (
                        <SelectItem key={niv.value} value={niv.value}>
                          {niv.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Places max</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={newGroupe.places_max}
                    onChange={(e) => setNewGroupe({...newGroupe, places_max: parseInt(e.target.value)})}
                    className="input-field"
                  />
                </div>
              </div>
              
              <div>
                <Label className="form-label">Notes</Label>
                <Textarea
                  value={newGroupe.notes}
                  onChange={(e) => setNewGroupe({...newGroupe, notes: e.target.value})}
                  placeholder="Informations complémentaires..."
                  className="input-field min-h-[80px]"
                />
              </div>
              
              <Button 
                className="btn-primary w-full" 
                onClick={handleCreateGroupe}
                data-testid="create-groupe-btn"
              >
                Créer le groupe
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <Input
          placeholder="Rechercher un groupe..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 input-field"
          data-testid="search-groupes"
        />
      </div>

      {/* Groups grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredGroupes.length === 0 ? (
          <div className="col-span-full">
            <div className="empty-state">
              <Users className="empty-state-icon" />
              <h3 className="empty-state-title">Aucun groupe</h3>
              <p className="empty-state-description">Créez votre premier groupe fixe</p>
            </div>
          </div>
        ) : (
          filteredGroupes.map(groupe => {
            const theme = getTheme(groupe.theme_principal_id);
            const instructeur = getInstructeur(groupe.instructeur_id);
            const jour = joursSemaine.find(j => j.value === groupe.jour_semaine);
            
            return (
              <Card 
                key={groupe.id}
                className="card-hover cursor-pointer"
                onClick={() => setSelectedGroupe(groupe)}
                data-testid={`groupe-card-${groupe.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold">{groupe.nom}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        {theme && (
                          <Badge 
                            style={{ backgroundColor: theme.couleur, color: 'white' }}
                          >
                            {theme.nom_abrege || theme.nom}
                          </Badge>
                        )}
                        <Badge variant="outline">
                          {niveaux.find(n => n.value === groupe.niveau)?.label || 'Tous niveaux'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Clock className="w-4 h-4" />
                    <span>{jour?.label} à {groupe.heure_debut}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Users className="w-4 h-4" />
                    <span>
                      {groupe.cavaliers_membres?.length || 0} / {groupe.places_max} cavaliers
                    </span>
                  </div>
                  {/* Liste des pseudos des cavaliers du groupe */}
                  {groupe.cavaliers_membres && groupe.cavaliers_membres.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {groupe.cavaliers_membres.slice(0, 5).map(cavId => {
                        const cav = cavaliers.find(c => c.id === cavId);
                        if (!cav) return null;
                        const displayName = cav.pseudo || `${cav.prenom} ${cav.nom?.[0]}.`;
                        return (
                          <Badge key={cavId} variant="secondary" className="text-xs">
                            {displayName}
                          </Badge>
                        );
                      })}
                      {groupe.cavaliers_membres.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{groupe.cavaliers_membres.length - 5}
                        </Badge>
                      )}
                    </div>
                  )}
                  {instructeur && (
                    <p className="text-sm text-stone-500">
                      Instructeur: {instructeur.prenom} {instructeur.nom}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Groupe detail modal */}
      {selectedGroupe && !isEditMode && (
        <Dialog open={!!selectedGroupe && !isEditMode} onOpenChange={() => setSelectedGroupe(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">{selectedGroupe.nom}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Groupe info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Thème</Label>
                  <div className="flex items-center gap-2">
                    {getTheme(selectedGroupe.theme_principal_id) && (
                      <>
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getTheme(selectedGroupe.theme_principal_id).couleur }}
                        />
                        <span className="font-medium">{getTheme(selectedGroupe.theme_principal_id).nom_complet || getTheme(selectedGroupe.theme_principal_id).nom}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Horaire</Label>
                  <p className="font-medium">
                    {joursSemaine.find(j => j.value === selectedGroupe.jour_semaine)?.label} {selectedGroupe.heure_debut}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Instructeur</Label>
                  <p className="font-medium">
                    {getInstructeur(selectedGroupe.instructeur_id)?.prenom} {getInstructeur(selectedGroupe.instructeur_id)?.nom}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Niveau</Label>
                  <p className="font-medium">
                    {niveaux.find(n => n.value === selectedGroupe.niveau)?.label || 'Tous niveaux'}
                  </p>
                </div>
              </div>
              
              {selectedGroupe.notes && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-sm text-stone-600">{selectedGroupe.notes}</p>
                </div>
              )}
              
              {/* Members list */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">
                    Cavaliers ({selectedGroupe.cavaliers_membres?.length || 0}/{selectedGroupe.places_max})
                  </h4>
                  {(selectedGroupe.cavaliers_membres?.length || 0) < selectedGroupe.places_max && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsAddMemberOpen(true)}
                      data-testid="add-member-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  )}
                </div>
                
                {selectedGroupe.cavaliers_membres?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedGroupe.cavaliers_membres.map(cavId => {
                      const cav = getCavalier(cavId);
                      if (!cav) return null;
                      const displayPseudo = cav.pseudo || `${cav.prenom} ${cav.nom?.[0]}.`;
                      return (
                        <div 
                          key={cavId}
                          className="flex items-center justify-between p-2 bg-stone-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center text-xs font-medium">
                              {displayPseudo.substring(0, 2).toUpperCase()}
                            </div>
                            <p className="font-medium text-sm">
                              {displayPseudo} <span className="text-stone-500 font-normal">({cav.prenom} {cav.nom})</span>
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveMember(cavId)}
                            data-testid={`remove-member-${cavId}`}
                          >
                            <UserMinus className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-stone-400 text-center py-4">Aucun cavalier dans ce groupe</p>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => openEditMode(selectedGroupe)}
                  data-testid="edit-groupe-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                {/* Delete button - only for instructeur_admin */}
                {user?.role_club === 'instructeur_admin' && (
                  <Button 
                    variant="destructive"
                    onClick={() => handleDeleteGroupe(false)}
                    data-testid="delete-groupe-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmOpen && selectedGroupe && (
        <Dialog open={deleteConfirmOpen} onOpenChange={() => setDeleteConfirmOpen(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Confirmer la suppression
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <p className="text-stone-600">
                Le groupe <strong>{selectedGroupe.nom}</strong> est utilisé par des cours existants.
              </p>
              <p className="text-sm text-stone-500">
                En supprimant ce groupe, les cours concernés ne seront plus associés à aucun groupe fixe.
              </p>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setDeleteConfirmOpen(false)}
                >
                  Annuler
                </Button>
                <Button 
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleDeleteGroupe(true)}
                  data-testid="confirm-delete-groupe-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer quand même
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit groupe modal */}
      {isEditMode && selectedGroupe && (
        <Dialog open={isEditMode} onOpenChange={() => { setIsEditMode(false); resetForm(); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Modifier le groupe</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="form-label">Nom du groupe *</Label>
                <Input
                  value={newGroupe.nom}
                  onChange={(e) => setNewGroupe({...newGroupe, nom: e.target.value})}
                  className="input-field"
                />
              </div>
              
              <div>
                <Label className="form-label">Thème principal *</Label>
                <Select 
                  value={newGroupe.theme_principal_id} 
                  onValueChange={(v) => setNewGroupe({...newGroupe, theme_principal_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un thème" />
                  </SelectTrigger>
                  <SelectContent>
                    {themes.map(theme => (
                      <SelectItem key={theme.id} value={theme.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: theme.couleur }}
                          />
                          {theme.nom_complet || theme.nom}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="form-label">Instructeur *</Label>
                <Select 
                  value={newGroupe.instructeur_id} 
                  onValueChange={(v) => setNewGroupe({...newGroupe, instructeur_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un instructeur" />
                  </SelectTrigger>
                  <SelectContent>
                    {instructeurs.map(instr => (
                      <SelectItem key={instr.id} value={instr.id}>
                        {instr.prenom} {instr.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Jour</Label>
                  <Select 
                    value={String(newGroupe.jour_semaine)} 
                    onValueChange={(v) => setNewGroupe({...newGroupe, jour_semaine: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {joursSemaine.map(jour => (
                        <SelectItem key={jour.value} value={String(jour.value)}>
                          {jour.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Heure</Label>
                  <Input
                    type="time"
                    value={newGroupe.heure_debut}
                    onChange={(e) => setNewGroupe({...newGroupe, heure_debut: e.target.value})}
                    className="input-field"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Niveau</Label>
                  <Select 
                    value={newGroupe.niveau} 
                    onValueChange={(v) => setNewGroupe({...newGroupe, niveau: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {niveaux.map(niv => (
                        <SelectItem key={niv.value} value={niv.value}>
                          {niv.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Places max</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={newGroupe.places_max}
                    onChange={(e) => setNewGroupe({...newGroupe, places_max: parseInt(e.target.value)})}
                    className="input-field"
                  />
                </div>
              </div>
              
              <div>
                <Label className="form-label">Notes</Label>
                <Textarea
                  value={newGroupe.notes}
                  onChange={(e) => setNewGroupe({...newGroupe, notes: e.target.value})}
                  className="input-field min-h-[80px]"
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setIsEditMode(false); resetForm(); }}
                >
                  Annuler
                </Button>
                <Button 
                  className="btn-primary flex-1" 
                  onClick={handleUpdateGroupe}
                  data-testid="update-groupe-btn"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add member modal */}
      {isAddMemberOpen && selectedGroupe && (
        <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif">Ajouter un cavalier</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-2 max-h-96 overflow-y-auto mt-4">
              {availableCavaliers.length === 0 ? (
                <p className="text-center text-stone-500 py-4">Aucun cavalier disponible</p>
              ) : (
                availableCavaliers.map(cav => {
                  const displayPseudo = cav.pseudo || `${cav.prenom} ${cav.nom?.[0]}.`;
                  return (
                    <div 
                      key={cav.id}
                      className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer transition-colors"
                      onClick={() => {
                        handleAddMember(cav.id);
                        setIsAddMemberOpen(false);
                      }}
                      data-testid={`add-cavalier-${cav.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center font-medium">
                          {displayPseudo.substring(0, 2).toUpperCase()}
                        </div>
                        <p className="font-medium">
                          {displayPseudo} <span className="text-stone-500 font-normal">({cav.prenom} {cav.nom})</span>
                        </p>
                      </div>
                      <UserPlus className="w-5 h-5 text-[#2E4A3D]" />
                    </div>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
